<script setup lang="ts">
import type { FormKitNode } from '@formkit/core'
import Check from '@/assets/icon-check.svg?component'

const { status, data: user } = useAuth()
const { addQuestion } = useProductsStore()

const {
  mainId
} = assertInject(ProductInjection)

const initialState = shallowRef<Partial<FaqForm>>({})

if (status.value === 'authenticated' && user.value) {
  initialState.value = {
    email: user.value.email,
    title: user.value.firstName !== '-' ? user.value.firstName : undefined
  }
}

const initialFocus = ref<HTMLElement | null>(null)
const submitted = shallowRef(false)

const setInitialFocus = (el: any) =>
  initialFocus.value = document.getElementById(el?.node.props.id)

const emit = defineEmits<{
  (e: 'submitting', state: boolean): void
}>()

const onSubmit = async (form: FaqForm, node: FormKitNode) => {
  emit('submitting', true)
  try {
    await addQuestion({ id: toValue(mainId), form })
    
    submitted.value = true
    node.reset({})
  } catch (e) {
    node.setErrors(
      JSON.stringify(e)
    )
  } finally {
    emit('submitting', false)
  }
}

defineExpose({
  initialFocus,
  submitted
})
</script>

<template>
  <FormKit
    type="form"
    id="faq-form"
    :plugins="[scrollToErrors()]"
    :disabled="submitted"
    :value="initialState"
    #default="{ value, disabled, state: { loading } }"
    :actions="false" 
    :incomplete-message="false"
    @submit="onSubmit"
  >
    <FormKit
      :ref="setInitialFocus"
      type="text"
      id="title"
      outer-class="mb-[10px]"
      name="title"
      label="Imię*"
      autocomplete="name"
      placeholder="Imię*"
      validation="required|length:3:64"
      validation-visibility="blur"
      :validation-messages="{
        required: 'Pole imię nie może być puste.',
        length: 'Pole imię może zawierać od 3 do 64 znaków.'
      }"
    />

    <FormKit
      type="email"
      outer-class="mb-[10px]"
      name="email"
      autocomplete="email"
      label="E-mail*"
      placeholder="E-mail*"
      validation="required|email"
      validation-visibility="blur"
      :validation-messages="{
        required: 'Pole e-mail nie może być puste.',
        email: 'Błędny adres e-mail.'
      }"
    />

    <FormKit
      type="textarea"
      name="content"
      label="Pytania*"
      placeholder="Pytania*"
      outer-class="mb-[10px]"
      rows="5"
      :help="`${value?.content ? value?.content.length : 0} / 2000`"
      validation="required|length:0,2000"
      validation-visibility="blur"
      :validation-messages="{
        required: 'Pole pytania nie może być puste.',
        length: 'Pole pytania nie może zawierać więcej niż 2000 znaków.',
      }"
    />

    <FormKit
      type="checkbox"
      outer-class="mb-[10px]"
      name="rules"
      validation="accepted"
      validation-visibility="blur"
      :validation-messages="{
        accepted: 'Zaakceptuj regulamin.'
      }"
    >
      <template #suffix>
        <div>
          Wyrażam zgodę na przetwarzanie moich danych osobowych podanych w formularzu przez EMOTIVO sp. z o. o. w Puławach (24-100), ul. 6 Sierpnia 1, w sposób opisany w
          <NuxtLink to="/i/polityka-prywatnosci" target="_blank" class="underline">polityce prywatności</NuxtLink>
          w celu uzyskania odpowiedzi na pytanie. Zgoda jest dobrowolna i może być w każdej chwili wycofana, kierując wiadomość na adres e-mail administratora. Wycofanie zgody nie wpływa na zgodność z prawem przetwarzania, którego dokonano na podstawie zgody przed jej wycofaniem.*
        </div>
      </template>
    </FormKit>

    <template v-if="submitted" >
      <div class="flex bg-green items-center gap-[20px] text-white px-[20px] py-[10px] mt-[20px]">
        <Check class="shrink-0" />
        Wiadomość została wysłana.
      </div>

      <Dialog.Close type="button" class="btn btn-action relative mt-[5px]">
        <i class="icon-arrow-button text-10 mr-[5px] -rotate-180" /> Wróć do produktu
      </Dialog.Close>
    </template>
    <FormKit
      v-else
      type="submit"
      :disabled="Boolean(disabled)"
      input-class="btn btn-action relative disabled:pointer-events-none disabled:bg-action-disabled mt-[5px]"
    >
      <Transition enter-active-class="animate-zoom-in" leave-active-class="animate-zoom-out">
        <Spinner v-if="loading" class="w-[20px] h-[20px]" />
        <span v-else class="inline-flex flex-row flex-nowrap items-center justify-center gap-[5px]">
          Wyślij <i class="icon-arrow-button text-10" />
        </span>
      </Transition>
    </FormKit>
  </FormKit>
</template>